import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listProfiles, listUsers, updateUser } from "../services/user";
import { listTeachers } from "../services/teacher";
import { listWorkers } from "../services/worker";
import { getUserInfo, getSucursalInfo } from "../services/auth";

const PROFESSOR_DESCRIPTION = "Professor(a)";

// Ported from sge_infantil_web/src/pages/user/edit/index.js. Differences
// from the create wizard: email isn't required, contact stays editable even
// without re-picking a teacher/worker, and there's no username/password or
// success screen — this saves in place like EditarFuncionarioLayer does.
const fieldsFromUser = (user, profiles) => ({
  profileId: profiles.find((p) => p.description === user?.profile?.description)?.id || "",
  selectedTeacher: user?.teacher?.id || "",
  selectedWorker: user?.worker?.id || "",
  name: user?.name || "",
  email: user?.email || "",
  contact: user?.contact || "",
  sucursais: Array.isArray(user?.sucursals) ? user.sucursals.map((s) => String(s.id)) : [],
});

const EditarUtilizadorLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [form, setForm] = useState(fieldsFromUser(location.state?.user, []));
  const initializedRef = useRef(false);

  useEffect(() => {
    listProfiles()
      .then((resp) => setProfiles(resp.data || []))
      .catch(() => setProfiles([]));
    listTeachers()
      .then((resp) => setTeachers(resp.data || []))
      .catch(() => setTeachers([]));
    listWorkers()
      .then((resp) => setWorkers(resp.data || []))
      .catch(() => setWorkers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setLoading(false);
      return;
    }
    listUsers()
      .then((resp) => {
        const found = (resp.data || []).find((u) => String(u.id) === String(id));
        setUser(found || null);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Prefills the form once both the user record and the profiles list are
  // available (they load independently) — runs only the first time so it
  // doesn't clobber in-progress edits once teachers/workers finish loading.
  useEffect(() => {
    if (initializedRef.current) return;
    if (!user || !profiles.length) return;
    setForm(fieldsFromUser(user, profiles));
    initializedRef.current = true;
  }, [user, profiles]);

  const selectedProfile = profiles.find((p) => String(p.id) === String(form.profileId));
  const isProfessor = selectedProfile?.description === PROFESSOR_DESCRIPTION;

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSelectProfile = (e) => {
    const profileId = e.target.value;
    setForm((f) => ({ ...f, profileId, selectedTeacher: "", selectedWorker: "" }));
  };

  const handleSelectTeacher = (e) => {
    const teacherId = e.target.value;
    const teacher = teachers.find((t2) => String(t2.id) === String(teacherId));
    setForm((f) => ({
      ...f,
      selectedTeacher: teacherId,
      name: teacher?.name || f.name,
      email: teacher?.email || f.email,
      contact: teacher?.contact || f.contact,
    }));
  };

  const handleSelectWorker = (e) => {
    const workerId = e.target.value;
    const worker = workers.find((w) => String(w.id) === String(workerId));
    setForm((f) => ({
      ...f,
      selectedWorker: workerId,
      name: worker?.name || f.name,
      email: worker?.email || f.email,
      contact: worker?.contact || f.contact,
    }));
  };

  const handleSucursaisChange = (e) => {
    const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    setForm((f) => ({ ...f, sucursais: values }));
  };

  const handleSave = async () => {
    if (!form.profileId || !form.name || !form.contact) {
      toast.error(t("validation.required"));
      return;
    }
    if (isProfessor && !form.selectedTeacher) {
      toast.error(t("utilizador.validation.teacher.required"));
      return;
    }
    if (!isProfessor && !form.selectedWorker) {
      toast.error(t("utilizador.validation.worker.required"));
      return;
    }
    if (form.contact.length !== 9) {
      toast.error(t("utilizador.validation.contact.length"));
      return;
    }

    setSaving(true);
    const userInfo = getUserInfo();
    const teacher = teachers.find((t2) => String(t2.id) === String(form.selectedTeacher));
    const worker = workers.find((w) => String(w.id) === String(form.selectedWorker));
    const availableSucursals = Array.isArray(userInfo?.sucursals) ? userInfo.sucursals : [];
    const resolvedSucursals = form.sucursais
      .map((sid) => availableSucursals.find((s) => String(s.id) === String(sid)))
      .filter(Boolean);

    const payload = {
      sucursalId: getSucursalInfo()?.id,
      name: form.name,
      email: form.email,
      contact: form.contact,
      address: "",
      profile: selectedProfile,
      sucursals: resolvedSucursals,
      ...(teacher ? { teacher: { name: teacher.name, id: teacher.id }, teacherId: teacher.id } : {}),
      ...(worker ? { worker: { name: worker.name, id: worker.id }, workerId: worker.id } : {}),
      updatedBy: userInfo?.id,
    };

    try {
      await updateUser(id, payload);
      toast.success(t("utilizador.edit.success"));
      navigate("/utilizadores/lista");
    } catch (err) {
      toast.error(t("global.error.message"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className='card'><div className='card-body text-secondary-light'>{t("utilizador.list.loading")}</div></div>;
  }

  if (!user) {
    return (
      <div className='card'>
        <div className='card-body text-center py-64'>
          <p className='text-secondary-light'>{t("utilizador.edit.not.found")}</p>
          <button
            type='button'
            className='btn btn-primary-600 px-32'
            onClick={() => navigate("/utilizadores/lista")}
          >
            <i className='ri-arrow-left-line' /> {t("utilizador.edit.back.to.list")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-lg-4'>
        <div className='user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100'>
          <div
            className='w-100'
            style={{
              height: 200,
              background: 'linear-gradient(135deg, var(--primary-600), var(--primary-300))',
            }}
          />
          <div className='pb-24 ms-16 mb-24 me-16 mt--100'>
            <div className='text-center border border-top-0 border-start-0 border-end-0'>
              <Icon
                icon='bi:person-circle'
                className='border br-white border-width-2-px w-200-px h-200-px rounded-circle text-secondary-light bg-neutral-100'
              />
              <h6 className='mb-0 mt-16'>{user.name}</h6>
              <span className='text-secondary-light mb-16'>{user.email || "-"}</span>
            </div>
            <div className='mt-24'>
              <h6 className='text-xl mb-16'>{t("global.personal.information")}</h6>
              <ul>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("utilizador.field.profile")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {user.profile?.description || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("utilizador.field.username")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {user.username || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.contact")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {user.contact || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.status")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {String(user.active) === "1" ? t("global.active") : t("global.inactive")}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className='col-lg-8'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='text-md text-primary-light mb-16'>{t("utilizador.edit.title")}</h6>
            <p className='text-secondary-light text-sm mb-24'>{t("utilizador.edit.subtitle")}</p>

            <div className='row'>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("utilizador.field.profile")} <span className='text-danger-600'>*</span>
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.profileId}
                    onChange={handleSelectProfile}
                  >
                    <option value=''>{t("utilizador.select.placeholder")}</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {isProfessor && (
                <div className='col-sm-6'>
                  <div className='mb-20'>
                    <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                      {t("utilizador.field.teacher")} <span className='text-danger-600'>*</span>
                    </label>
                    <select
                      className='form-control radius-8 form-select'
                      value={form.selectedTeacher}
                      onChange={handleSelectTeacher}
                    >
                      <option value=''>{t("utilizador.select.placeholder")}</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {!isProfessor && form.profileId && (
                <div className='col-sm-6'>
                  <div className='mb-20'>
                    <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                      {t("utilizador.field.worker")} <span className='text-danger-600'>*</span>
                    </label>
                    <select
                      className='form-control radius-8 form-select'
                      value={form.selectedWorker}
                      onChange={handleSelectWorker}
                    >
                      <option value=''>{t("utilizador.select.placeholder")}</option>
                      {workers.map((worker) => (
                        <option key={worker.id} value={worker.id}>
                          {worker.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("utilizador.field.name")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="f7:person" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.name}
                      onChange={setField("name")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("utilizador.field.email")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="mage:email" />
                    </span>
                    <input
                      type='email'
                      className='form-control radius-8'
                      value={form.email}
                      onChange={setField("email")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("utilizador.field.contact")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className='input-group'>
                    <span className='input-group-text'>+258</span>
                    <input
                      type='text'
                      maxLength={9}
                      className='form-control radius-8'
                      value={form.contact}
                      onChange={setField("contact")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-12'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("utilizador.field.sucursais")} <span className='text-danger-600'>*</span>
                  </label>
                  <select
                    multiple
                    className='form-control radius-8 form-select'
                    style={{ height: 130 }}
                    value={form.sucursais}
                    onChange={handleSucursaisChange}
                  >
                    {(Array.isArray(getUserInfo()?.sucursals) ? getUserInfo().sucursals : []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.description || s.name}
                      </option>
                    ))}
                  </select>
                  <span className='text-secondary-light text-sm'>
                    {t("utilizador.field.sucursais.hint")}
                  </span>
                </div>
              </div>
            </div>

            <div className='d-flex align-items-center justify-content-center gap-3'>
              <button
                type='button'
                className='border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8'
                onClick={() => navigate("/utilizadores/lista")}
                disabled={saving}
              >
                {t("global.cancel")}
              </button>
              <button
                type='button'
                className='btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "..." : <><i className='ri-save-line' /> {t("global.save")}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditarUtilizadorLayer;
